import { ComboboxItem } from '@mantine/core';

import { AppointmentFormOptions, AppointmentTypeFormOption } from '@/models/appointment/BookingFormOptions.ts';
import {
    EnrichedAppointmentType,
    EnrichedPractitionerData,
    Practice,
    Practitioner,
} from '@/models/appointment/EnrichedPractitionerData.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';
import { AppointmentType } from '@/models/doc-cirrus/DCScheduleTypeMapping.ts';
import type { InsuranceType } from '@/models/insurance.ts';

import type { AppointmentChainConfiguration } from '@/schemas/admin/appointment-chain-configurations.schema.ts';

export const getPractitionerByCalendarId = (
    practitioners: Practitioner[],
    calendar_pvs_id: string | undefined
): Practitioner | undefined => {
    if (!calendar_pvs_id) return undefined;
    return practitioners.find((p) => p.calendars.some((c) => c.pvs.id === calendar_pvs_id));
};

export const findAppointmentTypeInInstance = (
    practitionerData: EnrichedPractitionerData[],
    instanceId: string,
    appointmentTypeId: string
): EnrichedAppointmentType | undefined =>
    practitionerData
        .filter((instance) => instance.instance_id === instanceId)
        .flatMap((instance) => instance.appointment_types ?? [])
        .find((apt) => apt.id === appointmentTypeId);

export const findPractitionerInInstanceByCalendar = (
    practitionerData: EnrichedPractitionerData[],
    instanceId: string,
    calendarId: string
): Practitioner | undefined =>
    getPractitionerByCalendarId(
        practitionerData
            .filter((instance) => instance.instance_id === instanceId)
            .flatMap((instance) => instance.practitioners ?? []),
        calendarId
    );

export const filterPractitionersByLocation = (
    practitioners: Practitioner[],
    location_key: string | undefined
): Practitioner[] =>
    practitioners.filter((p) => p.practices.some((practice) => practice.prismic_hub.key === location_key));

export const buildPracticeOptions = (
    practices: Practice[],
    practitioners_at_location: Practitioner[]
): ComboboxItem[] =>
    practices
        .filter((practice) =>
            practitioners_at_location.some((p) => p.practices.some((pp) => pp.pvs?.id === practice.pvs?.id))
        )
        .map((practice) => ({ value: practice.pvs?.id ?? '', label: practice.pvs?.name ?? '' }));

// TODO: add unit test
export const getFormOptions = (
    data: EnrichedPractitionerData,
    selected_calendar_id: string | undefined,
    location_key: string | undefined
): AppointmentFormOptions => {
    const practitioners = filterPractitionersByLocation(data.practitioners, location_key);
    const result: AppointmentFormOptions = {
        all_practices: [],
        appointment_types: data.appointment_types.map((apt_type): AppointmentTypeFormOption => {
            const is_offered = selected_calendar_id
                ? apt_type.pvs.pvs_calendar_ids.includes(selected_calendar_id)
                : undefined;

            const is_notification_enabled = selected_calendar_id
                ? getPractitionerByCalendarId(data.practitioners, selected_calendar_id)?.is_notification_enabled
                : false;

            return {
                value: apt_type.id,
                label: apt_type.name,
                is_mapped: true,
                is_offered: is_offered,
                default_duration: apt_type.duration,
                is_notification_enabled: is_notification_enabled,
                appointment_type: apt_type.type,
            };
        }),
        unmapped_schedule_types: data.unmapped_schedule_types.map(
            (schedule_type): AppointmentTypeFormOption => ({
                value: schedule_type.pvs.pvs_schedule_type_id,
                label: schedule_type.name,
                is_mapped: false,
                is_offered: selected_calendar_id
                    ? schedule_type.pvs.pvs_calendar_ids.includes(selected_calendar_id)
                    : undefined,
                default_duration: schedule_type.duration,
                is_notification_enabled: false, // no notification for unmapped schedule types
                appointment_type: AppointmentType.SINGLE, // unmapped schedule types are always single
            })
        ),
        practitioners: practitioners.map((practitioner) => ({
            id: practitioner.id,
            name: practitioner.name,
            calendars: practitioner.calendars.map((calendar) => ({
                value: calendar.pvs.id,
                label: calendar.pvs.name,
            })),
        })),
    };

    if (data.pvs_provider === PVSProvider.DOC_CIRRUS) {
        result.all_practices = buildPracticeOptions(data.practices, practitioners);
    }

    return result;
};

/**
 * id: schedule_type_id or appointment_type_id
 */
export const getDefaultDurationFromAppointmentTypeId = (
    id: string,
    form_options: AppointmentFormOptions
): number | undefined => {
    const appointments = [...form_options.appointment_types, ...form_options.unmapped_schedule_types];

    const duration = appointments.find((apt) => apt.value === id)?.default_duration;

    // TODO: support different duration unit
    if (!duration || duration.unit !== 'MINUTES') {
        return undefined;
    }

    return duration.value;
};

export const collection_appointment_count_options = Array.from({ length: 19 }, (_, i) => ({
    value: String(i + 2),
    label: `${i + 2} Termine`,
}));

export const collection_frequency_options = Array.from({ length: 8 }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1),
}));

export const APPOINTMENT_MODE = {
    SINGLE: 'single',
    SERIES: 'series',
    CHAINS: 'chains',
} as const;
export type AppointmentMode = (typeof APPOINTMENT_MODE)[keyof typeof APPOINTMENT_MODE];

interface AppointmentModeSelectorItem {
    label: string;
    value: AppointmentMode;
    disabled?: boolean;
    disableText?: string;
}

export const MULTIPLE_BOOKINGS_UNAVAILABLE_TEXT = 'Für Mehrfachbuchungen nicht verfügbar.';

export const getAppointmentModeSelectorData = (
    hasSeriesTypes: boolean,
    hasChainTypes: boolean,
    bookingMultiple: boolean
): AppointmentModeSelectorItem[] => {
    const bookingMultipleText = MULTIPLE_BOOKINGS_UNAVAILABLE_TEXT;
    return [
        { label: 'Einzeltermin', value: APPOINTMENT_MODE.SINGLE },
        {
            label: 'Terminserie',
            value: APPOINTMENT_MODE.SERIES,
            disabled: bookingMultiple || !hasSeriesTypes,
            disableText: bookingMultiple
                ? bookingMultipleText
                : 'Serientermine sind noch nicht eingerichtet. Bitte wenden Sie sich an ETERNO, um diese Funktion einzurichten.',
        },
        {
            label: 'Kettentermine',
            value: APPOINTMENT_MODE.CHAINS,
            disabled: bookingMultiple || !hasChainTypes,
            disableText: bookingMultiple ? bookingMultipleText : 'Für diese Auswahl sind keine Terminketten verfügbar.',
        },
    ];
};

export const buildAppointmentTypeDetailMap = (
    data: EnrichedPractitionerData[] | undefined
): Record<string, { name: string; durationMinutes: number }> => {
    const map: Record<string, { name: string; durationMinutes: number }> = {};
    if (!data) return map;
    for (const instance of data) {
        for (const type of instance.appointment_types ?? []) {
            if (Object.hasOwn(map, type.id)) continue;
            if (type.duration.unit !== 'MINUTES') continue;
            map[type.id] = { name: type.name, durationMinutes: type.duration.value };
        }
    }
    return map;
};

interface CalendarOptionGroup {
    id: string;
    name: string;
    calendars: { id: string; name: string }[];
}

export const buildCalendarOptionsByMode = (
    formOptions: AppointmentFormOptions | undefined,
    data: EnrichedPractitionerData | undefined,
    isSeriesMode: boolean,
    lockedCalendarId?: string
): CalendarOptionGroup[] => {
    if (!formOptions) return [];

    const visibleCalendarIds = new Set<string>(
        formOptions.practitioners.flatMap((p) => p.calendars.map((c) => c.value))
    );

    const modeCalendarIds = new Set<string>();
    for (const aptType of data?.appointment_types ?? []) {
        const matches = isSeriesMode
            ? aptType.type === AppointmentType.SERIES
            : aptType.type === AppointmentType.SINGLE;
        if (!matches) continue;
        for (const id of aptType.pvs.pvs_calendar_ids) {
            if (visibleCalendarIds.has(id)) modeCalendarIds.add(id);
        }
    }
    if (!isSeriesMode) {
        for (const scheduleType of data?.unmapped_schedule_types ?? []) {
            for (const id of scheduleType.pvs.pvs_calendar_ids) {
                if (visibleCalendarIds.has(id)) modeCalendarIds.add(id);
            }
        }
    }
    if (lockedCalendarId) modeCalendarIds.add(lockedCalendarId);

    return formOptions.practitioners
        .map((p) => ({
            id: p.id,
            name: p.name,
            calendars: p.calendars
                .filter((c) => modeCalendarIds.has(c.value))
                .map((c) => ({ id: c.value, name: c.label })),
        }))
        .filter((p) => p.calendars.length > 0);
};

interface CalendarOptionsFilters {
    instanceId?: string;
    appointmentTypeId?: string;
}

export const buildCalendarOptions = (
    data: EnrichedPractitionerData[] | undefined,
    filters?: CalendarOptionsFilters
): { value: string; label: string }[] => {
    if (!data) return [];
    const seen = new Set<string>();
    const options: { value: string; label: string }[] = [];
    for (const instance of data) {
        if (filters?.instanceId && instance.instance_id !== filters.instanceId) continue;
        for (const practitioner of instance.practitioners ?? []) {
            for (const calendar of practitioner.calendars ?? []) {
                if (filters?.appointmentTypeId && !calendar.appointment_type_ids?.includes(filters.appointmentTypeId)) {
                    continue;
                }
                if (seen.has(calendar.pvs.id)) continue;
                seen.add(calendar.pvs.id);
                options.push({
                    value: calendar.pvs.id,
                    label: calendar.pvs.name ?? practitioner.name,
                });
            }
        }
    }
    return options;
};

interface IntermediateCalendarOption {
    value: string;
    practitionerId: string;
    practitionerName: string;
    calendarName: string | undefined;
}

export const buildCalendarOptionsByAptType = (
    data: EnrichedPractitionerData[] | undefined
): Record<string, { value: string; label: string }[]> => {
    const result: Record<string, { value: string; label: string }[]> = {};
    if (!data) return result;

    const intermediateByAptType: Record<string, IntermediateCalendarOption[]> = {};
    const seenByAptType: Record<string, Set<string>> = {};
    for (const instance of data) {
        for (const practitioner of instance.practitioners ?? []) {
            for (const calendar of practitioner.calendars ?? []) {
                for (const aptTypeId of calendar.appointment_type_ids ?? []) {
                    intermediateByAptType[aptTypeId] ??= [];
                    seenByAptType[aptTypeId] ??= new Set();
                    if (seenByAptType[aptTypeId].has(calendar.pvs.id)) continue;
                    seenByAptType[aptTypeId].add(calendar.pvs.id);
                    intermediateByAptType[aptTypeId].push({
                        value: calendar.pvs.id,
                        practitionerId: practitioner.id,
                        practitionerName: practitioner.name,
                        calendarName: calendar.pvs.name,
                    });
                }
            }
        }
    }

    for (const [aptTypeId, options] of Object.entries(intermediateByAptType)) {
        const calendarsPerPractitioner: Record<string, number> = {};
        for (const opt of options) {
            calendarsPerPractitioner[opt.practitionerId] = (calendarsPerPractitioner[opt.practitionerId] ?? 0) + 1;
        }
        result[aptTypeId] = options.map((opt) => ({
            value: opt.value,
            label:
                calendarsPerPractitioner[opt.practitionerId] === 1
                    ? opt.practitionerName
                    : (opt.calendarName ?? opt.practitionerName),
        }));
    }
    return result;
};

interface ChainConfigFilters {
    practiceId?: string;
    instanceId?: string;
    insuranceType?: InsuranceType;
    isReturning?: boolean;
}

export const filterChainConfigs = (
    configs: AppointmentChainConfiguration[],
    filters?: ChainConfigFilters
): AppointmentChainConfiguration[] => {
    if (!filters?.practiceId && !filters?.instanceId && !filters?.insuranceType && filters?.isReturning === undefined)
        return configs;
    return configs.filter((config) => {
        if (filters.insuranceType && !config.insurance_types.includes(filters.insuranceType)) return false;
        if (filters.instanceId && config.instance_id !== filters.instanceId) return false;
        const isDocCirrusChain = config.practice_id.length > 0;
        if (isDocCirrusChain && filters.practiceId && config.practice_id !== filters.practiceId) return false;
        if (filters.isReturning === true && !config.is_for_returning_patients) return false;
        if (filters.isReturning === false && !config.is_for_new_patients) return false;
        return true;
    });
};
